{
  "blocks": [
    {
      "id": "moon-article-uk-fertility-treatment-guide",
      "kind": "article",
      "tags": [
        "moon",
        "moon-guides",
        "moon-costs",
        "clinic",
        "practical",
        "ivf",
        "iui"
      ],
      "title": "Fertility treatment in the UK",
      "subtitle": "How NHS referral, local funding, HFEA clinic data, private costs, IUI, donor rules, consent, and treatment timelines fit together.",
      "readTimeMinutes": 11,
      "body": {
        "introduction": "Country guide",
        "quickSummary": [
          {
            "title": "There are two parallel routes.",
            "description": "You may move through an NHS pathway, a private pathway, or both. NHS access depends heavily on where you live and whether you meet local criteria."
          },
          {
            "title": "HFEA is the clinic source of record.",
            "description": "Licensed UK fertility clinics have HFEA profiles with inspection context and public outcomes when the figures are publishable."
          },
          {
            "title": "Letters and consent forms are real milestones.",
            "description": "UK patients often wait for referral, eligibility, funding, appointment, and treatment-start letters before a cycle can begin."
          }
        ],
        "sections": [
          {
            "title": "The UK pathway in plain English",
            "content": "Most people start with a GP, a hospital gynaecology or urology service, a private fertility assessment, or a direct private clinic consult. The first phase is usually investigation: ovulation and hormone blood tests, ultrasound or AFC, semen analysis, tubal testing when relevant, infection screening, and a review of medical history. After that, the route splits into NHS-funded care, private care, or a mixed pathway where some testing is NHS-funded and treatment is private.",
            "bulletPoints": [
              "NHS investigation can be available even when NHS-funded IVF is not",
              "Private clinics may still ask for GP or hospital records before treatment",
              "Same-sex couples and single patients may be asked to document donor insemination or IUI attempts before IVF funding decisions",
              "If a test shows IVF is the only realistic route, referral can move faster than a standard unexplained-infertility timeline"
            ]
          },
          {
            "title": "NHS funding is not one national offer",
            "content": "NICE gives national clinical recommendations, but NHS-funded fertility treatment is commissioned locally. Scotland, Wales, and Northern Ireland set their own rules. In England, local Integrated Care Boards decide eligibility and how much treatment is funded. That means age limits, BMI or smoking criteria, child-in-family rules, the number of funded cycles, medication coverage, embryo storage, and frozen transfer coverage can differ by postcode.",
            "bulletPoints": [
              "Ask which ICB policy applies to your GP registration address",
              "Ask what the local policy means by one full IVF cycle",
              "Check whether medication, storage, HFEA fees, and frozen transfers are included",
              "Ask whether previous private treatment affects NHS eligibility"
            ]
          },
          {
            "title": "The referral and letter sequence",
            "content": "In an NHS pathway, paperwork is part of the journey. A GP or hospital referral usually starts the specialist pathway. The clinic or hospital may then send an appointment letter, request missing tests, confirm whether funding approval is needed, and later send eligibility or next-step letters. Some units apply for funding after they confirm eligibility, then tell patients when funding is available. Treatment start often waits until consent forms, virology results, sperm assessment, teaching for injections, and funding approval are complete.",
            "bulletPoints": [
              "Referral letter or electronic referral",
              "Initial appointment letter or portal message",
              "Eligibility or funding approval letter when NHS-funded treatment applies",
              "HFEA consent forms for treatment, storage, and embryo or gamete use",
              "Treatment calendar or medication-start instructions once all prerequisites are cleared"
            ]
          },
          {
            "title": "How long it can take",
            "content": "The medical IVF cycle itself is often measured in weeks, but the UK pathway around it can be longer. Initial testing may take one or more menstrual cycles. NHS referral, local eligibility review, and funding approval can add weeks or months, depending on the service and waiting list. Once a clinic has cleared consent, infectious disease screening, semen assessment, and funding or payment, a treatment cycle commonly follows baseline, stimulation, trigger, egg collection, fertilisation, embryo transfer, and pregnancy testing.",
            "bulletPoints": [
              "Initial testing: often one cycle, longer if tubal or male-factor workup is needed",
              "Funding approval: variable; some hospital pages describe a process measured in weeks",
              "Stimulation to egg collection: commonly about 10 to 14 days",
              "Full IVF cycle: NHS patient guidance describes roughly 3 to 6 weeks for the treatment cycle itself",
              "Pregnancy test: commonly around 2 weeks after transfer or about 16 days after egg collection in some NHS unit instructions"
            ]
          },
          {
            "title": "Choosing a clinic with HFEA data",
            "content": "HFEA's Choose a Fertility Clinic service is the official public UK clinic source. It includes licensed clinic profiles, inspection context, patient ratings, treatments, and success-rate figures where HFEA can publish them. HFEA also warns that individual clinic rates cannot tell you your personal chance of a baby, and that figures for very small numbers of cycles may be suppressed because they can mislead.",
            "bulletPoints": [
              "Compare the same metric, age band, treatment type, and reporting period",
              "Use the national average only when it matches the metric on the clinic graph",
              "Check whether the figure is per embryo transfer, per egg collection, IUI pregnancy, or donor insemination",
              "For low-volume clinics, missing data may mean suppressed figures rather than poor outcomes"
            ]
          },
          {
            "title": "Private care: ask for the whole costed plan",
            "content": "Private fertility prices can look lower than the eventual bill if they exclude medication, scans, blood tests, sedation, HFEA licence fees, embryo freezing, embryo storage, donor sperm, ICSI, PGT, add-ons, or follow-up. Ask for a personalised written quote and ask the clinic to separate required medical items from optional add-ons.",
            "bulletPoints": [
              "Base IVF or ICSI package",
              "Medication estimate and monitoring visits",
              "Embryo freezing and annual storage",
              "Donor sperm, donor egg, or known donor coordination",
              "Counselling and consent appointments",
              "Add-ons such as time-lapse imaging, glue, scratch, tests, or sperm-selection tools"
            ]
          },
          {
            "title": "IUI and donor insemination in the UK",
            "content": "IUI is less invasive and usually less expensive than IVF, but it has lower success per cycle. HFEA describes IUI as common for people using donor sperm, including single women and female couples, and also possible for some heterosexual couples. NICE guidance changed in 2026 for some unexplained infertility situations, but NHS funding for IUI and the number of attempts required before IVF still depends on local policy and patient context.",
            "bulletPoints": [
              "Donor sperm treatment should be checked against HFEA clinic/licence context",
              "Some private diagnostics providers can do monitoring scans or blood work while an HFEA clinic manages treatment",
              "Ask whether the provider performs IUI, only monitors cycles, or refers to another clinic",
              "Ask whether partner sperm, donor sperm, stimulated IUI, and unstimulated IUI are handled differently"
            ]
          },
          {
            "title": "Consent, donor identity, and storage",
            "content": "Consent is a legal and practical step in UK fertility treatment. HFEA consent forms cover treatment, storage, embryo use, gamete use, disclosure, and what should happen in specific future scenarios. Donor treatment has additional UK-specific rules, including counselling expectations and identity-release rights for donor-conceived people when they become adults.",
            "bulletPoints": [
              "Do not treat consent forms as admin; they decide what can happen later",
              "Review storage periods, renewal reminders, and what happens if consent expires",
              "Discuss separation, death, serious illness, unused embryos, and future use before signing",
              "For donor treatment, ask about donor availability, wait times, counselling, sibling limits, and identity-release rules"
            ]
          },
          {
            "title": "What to ask at a UK consult",
            "content": "The useful UK consult questions are partly medical and partly logistical. You want to understand the diagnosis, the treatment recommendation, the regulatory context, the funding route, the timeline, and the exact next document or decision that unlocks treatment.",
            "bulletPoints": [
              "Are you HFEA-licensed for the treatment I need?",
              "Which HFEA metric best matches my planned treatment?",
              "If NHS-funded, which ICB or national criteria apply to me?",
              "What letters, approvals, consent forms, and tests are still needed?",
              "If private, what is included and excluded from the written quote?",
              "Do you provide IUI, donor insemination, monitoring only, or referral onward?",
              "How do you communicate results, embryo updates, and treatment instructions?"
            ]
          }
        ],
        "references": [
          {
            "authors": "Human Fertilisation and Embryology Authority",
            "title": "Choose a Fertility Clinic and Costs and funding"
          },
          {
            "authors": "NICE",
            "title": "Fertility problems: assessment and treatment, NG257, 2026"
          },
          {
            "authors": "NHS",
            "title": "IVF and Treatment for infertility patient guidance"
          },
          {
            "authors": "Guy's and St Thomas' NHS Foundation Trust",
            "title": "Assisted conception unit appointments"
          }
        ]
      }
    }
  ],
  "relationships": [],
  "localeVariants": []
}
